import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import UserProfileModal from './UserProfileModal';
import styled from 'styled-components';
import { clearPermissionsCache } from '../hooks/PermissionsContext';

const getInitials = (account) => {
  const given   = account?.idTokenClaims?.given_name?.[0] ?? '';
  const surname = account?.idTokenClaims?.family_name?.[0] ?? '';
  if (given || surname) return (given + surname).toUpperCase();
  const email = account?.username ?? '';
  return email.substring(0, 2).toUpperCase();
};

const AvatarButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: linear-gradient(135deg, #ff4757, #ff6b35);
  color: white;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
  transition: border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: white;
    transform: scale(1.05);
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: fixed;
  z-index: 9999;
  min-width: 220px;
  background: rgba(15, 15, 25, 0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 8px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const UserInfo = styled.div`
  padding: 12px 16px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 4px;
`;

const UserName = styled.div`
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2px;
`;

const UserEmail = styled.div`
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.78rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 4px 0;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
  }

  &.danger {
    color: rgba(255, 100, 100, 0.85);
    &:hover {
      background: rgba(255, 71, 87, 0.15);
      color: #ff4757;
    }
  }
`;

const UserAvatar = () => {
  const { instance, accounts } = useMsal();
  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  const account = accounts[0];
  const initials = getInitials(account);
  const displayName = account?.idTokenClaims?.name || account?.name || '';
  const email = account?.username || '';
  const handleAvatarClick = () => {
    if (!open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(prev => !prev);
  };

  const handleAccountSettings = () => {
    setOpen(false);
    setShowProfile(true);
  };

  const handleSignOut = () => {
    setOpen(false);
    clearPermissionsCache();
    instance.logoutRedirect({
      postLogoutRedirectUri: process.env.REACT_APP_ENTRA_REDIRECT_URI,
    });
  };

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <>
      <AvatarWrapper ref={wrapperRef}>
        <AvatarButton
          onClick={handleAvatarClick}
          title={displayName || email}
          aria-label="User menu"
          aria-haspopup="true"
          aria-expanded={open}
        >
          {initials || '?'}
        </AvatarButton>

        {open && ReactDOM.createPortal(
          <DropdownMenu ref={menuRef} style={{ top: menuPos.top, right: menuPos.right }}>
            <UserInfo>
              <UserName>{displayName}</UserName>
              <UserEmail title={email}>{email}</UserEmail>
            </UserInfo>
            <MenuItem onClick={handleAccountSettings}>
              👤 My Profile
            </MenuItem>
            <Divider />
            <MenuItem className="danger" onClick={handleSignOut}>
              🚪 Sign Out
            </MenuItem>
          </DropdownMenu>,
          document.body
        )}
      </AvatarWrapper>
      {showProfile && <UserProfileModal onClose={() => setShowProfile(false)} />}
    </>
  );
};

export default UserAvatar;
